/** Step 1 — what the statement is for and who swears it, then GENERATE WITH AI (the whole statement from the file, like "Fill with AI"
 *  on the LBA) — or, tucked away, build it by hand from the template blocks. Also lists the statements already on the file. */
import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Copy, FileText, Loader2, Lock, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { cloneWitnessStatement, createWitnessStatement, deleteWitnessStatement } from "@/lib/api";
import { STAGE_TONE, errMsg, fmtWhen } from "./witnessShared";

function StageTiles({ stages, value, onChange, disabled }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="ws-stages">
      {stages.map((s) => (
        <button key={s.key} type="button" disabled={disabled} onClick={() => onChange(s.key)} data-testid={`ws-stage-${s.key}`} aria-pressed={value === s.key}
          className={cn("text-left border rounded-md p-2.5 transition-colors", value === s.key ? STAGE_TONE[s.key] : "border-slate-200 hover:bg-slate-50", disabled && "opacity-60")}>
          <div className="text-xs font-semibold">{s.label}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">{s.short} · {s.we} v {s.they}</div>
          <div className="text-[10px] text-slate-500 mt-1 line-clamp-2">{s.framework[0]}</div>
        </button>
      ))}
    </div>
  );
}

function BlockPicker({ blocks, stage, picked, setPicked }) {
  const rows = useMemo(() => blocks.filter((b) => b.stages === "*" || b.stages.includes(stage)), [blocks, stage]);
  const groups = useMemo(() => [...new Set(rows.map((b) => b.group))].map((g) => ({ g, rows: rows.filter((b) => b.group === g) })), [rows]);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <div className="border rounded divide-y max-h-56 overflow-y-auto bg-white" data-testid="ws-blocks">
      {groups.map(({ g, rows: rs }) => (
        <div key={g} className="p-2">
          <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-1">{g}</div>
          {rs.map((b) => (
            <label key={b.id} className="flex items-start gap-2 py-0.5 text-[11px] cursor-pointer" data-testid={`ws-block-${b.id}`} title={b.text}>
              <Checkbox checked={picked.includes(b.id)} onCheckedChange={() => toggle(b.id)} className="mt-0.5" />
              <span className="min-w-0 flex-1">{b.title} <span className="text-slate-400">· {b.kind}</span></span>
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

function ExistingList({ caseId, statements, onOpen, onChanged, disabled }) {
  const [busy, setBusy] = useState("");
  if (!statements.length) return null;
  const act = async (id, fn, ok) => { setBusy(id); try { const r = await fn(); toast.success(ok); onChanged(r); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  return (
    <div className="border rounded divide-y" data-testid="ws-existing">
      <div className="px-2 py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wide">Statements on this file</div>
      {statements.map((w) => (
        <div key={w.id} className="flex items-center gap-2 px-2 py-1.5 text-[11px]" data-testid={`ws-existing-${w.id}`}>
          {w.status === "final" ? <Lock className="w-3 h-3 text-emerald-600 shrink-0" /> : <FileText className="w-3 h-3 text-slate-400 shrink-0" />}
          <span className="min-w-0 flex-1 truncate">Statement {w.number || 1} of {w.deponent?.name} · <span className="text-slate-500">{w.stage_label}</span> · {w.status === "final" ? `final ${fmtWhen(w.final?.at)}` : `draft · saved ${fmtWhen(w.updated_at)}`}</span>
          <Button size="sm" variant="outline" className="h-6 text-[10px]" disabled={disabled} onClick={() => onOpen(w.id)} data-testid={`ws-open-${w.id}`}>{w.status === "final" ? "View" : "Continue"}</Button>
          <Button size="sm" variant="ghost" className="h-6 text-[10px]" title="Start a new version from this one" disabled={disabled || busy === w.id} onClick={() => act(w.id, () => cloneWitnessStatement(caseId, w.id), "New version created")} data-testid={`ws-clone-${w.id}`}>
            {busy === w.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />}
          </Button>
          {w.status !== "final" && (
            <Button size="sm" variant="ghost" className="h-6 text-[10px] text-rose-600" disabled={disabled || busy === w.id} onClick={() => { if (window.confirm("Delete this draft?")) act(w.id, () => deleteWitnessStatement(caseId, w.id), "Draft deleted"); }} data-testid={`ws-delete-${w.id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

/** The amber "Generate with AI" panel — one click reads the whole file and writes the statement. */
function GeneratePanel({ ctx, stage, deponentName, onGenerate, progress }) {
  const [instructions, setInstructions] = useState("");
  const running = !!progress;
  const st = ctx.stages.find((s) => s.key === stage);
  return (
    <div className="border rounded-md p-3 sm:p-4 bg-amber-50/60 border-amber-200 space-y-3" data-testid="ws-generate-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-800"><Sparkles className="w-4 h-4 text-amber-600 shrink-0" />Generate with AI</div>
        <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700 shrink-0" onClick={() => onGenerate(instructions)} disabled={running || !deponentName} data-testid="ws-generate">
          {running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}{running ? "Generating…" : "Generate with AI"}
        </Button>
      </div>
      <p className="text-xs leading-relaxed text-slate-700">
        GPT-5.5 reads the whole file — every note on the timeline, the Legal calendar, the logged emails, the documents (PDF text), the LoanDisk loan and repayment history, the Airtable row and the case map — and writes
        <b> {st?.label?.toLowerCase()}</b> in {deponentName?.split(" ")[0] || "the witness"}'s own words: numbered paragraphs in date order, the legal basis under {st?.regime === "insolvency" ? "the Insolvency Act 1986 and the Insolvency Rules 2016" : "the CPR"}, and the documents it relies on marked as exhibits ({(deponentName || "").split(" ").map((p) => p[0]).join("").toUpperCase() || "JA"}1, {(deponentName || "").split(" ").map((p) => p[0]).join("").toUpperCase() || "JA"}2 …).
        Anything the file does not show comes back as a <span className="bg-yellow-200 px-1 rounded">[placeholder]</span> highlighted yellow for you to replace. You review and edit every paragraph before anything is filed.
      </p>
      <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} disabled={running} rows={2} className="text-xs bg-white" data-testid="ws-generate-instructions"
        placeholder="Optional — anything the AI should know or do, e.g. 'the debtor says no demand was made — answer that first' · 'keep it under 20 paragraphs' · 'the balance on 1 Oct was £8,497.83'" />
      {running && (
        <div className="rounded border border-amber-200 bg-white p-2 text-[11px] text-amber-900 flex items-center gap-2" data-testid="ws-generate-progress">
          <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" /><span className="min-w-0 truncate">{progress}</span>
        </div>
      )}
    </div>
  );
}

export function WitnessSetup({ caseRow, ctx, presetStage, onCreated, onOpen, onListChanged, onGenerate, progress }) {
  const stageLabels = Object.fromEntries(ctx.stages.map((s) => [s.key, s.label]));
  const [stage, setStage] = useState(presetStage && stageLabels[presetStage] ? presetStage : ctx.stages[0].key);
  const [deponentName, setDeponentName] = useState(ctx.default_deponent.name);
  const [title, setTitle] = useState(ctx.default_deponent.title);
  const [byHand, setByHand] = useState(false);
  const [picked, setPicked] = useState(ctx.stages.find((s) => s.key === (presetStage || ctx.stages[0].key))?.default_blocks || []);
  const [creating, setCreating] = useState(false);
  const running = !!progress;
  const pickStage = (k) => { setStage(k); setPicked(ctx.stages.find((s) => s.key === k)?.default_blocks || []); };
  const deponent = { name: deponentName, title };
  const create = async (blockIds) => {
    setCreating(true);
    try { onCreated(await createWitnessStatement(caseRow.case_id, { stage, deponent, block_ids: blockIds })); }
    catch (e) { toast.error(errMsg(e)); } finally { setCreating(false); }
  };
  const statements = (ctx.statements || []).map((w) => ({ ...w, stage_label: stageLabels[w.stage] || w.stage }));
  return (
    <div className="space-y-3">
      <ExistingList caseId={caseRow.case_id} statements={statements} onOpen={onOpen} onChanged={onListChanged} disabled={running} />
      <div>
        <div className="text-[11px] font-medium text-slate-600 mb-1">What is this statement for?</div>
        <StageTiles stages={ctx.stages} value={stage} onChange={pickStage} disabled={running} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block space-y-1"><span className="text-[11px] font-medium text-slate-600">Witness (from the Legal roster)</span>
          <select className="w-full h-8 rounded-md border border-input bg-white px-2 text-xs" value={deponentName} disabled={running} onChange={(e) => setDeponentName(e.target.value)} data-testid="ws-deponent">
            {ctx.deponents.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
          </select>
        </label>
        <label className="block space-y-1"><span className="text-[11px] font-medium text-slate-600">Job title (as it appears in the statement)</span>
          <Input value={title} disabled={running} onChange={(e) => setTitle(e.target.value)} className="h-8 text-xs" data-testid="ws-deponent-title" />
        </label>
      </div>
      <GeneratePanel ctx={ctx} stage={stage} deponentName={deponentName} progress={progress} onGenerate={(instructions) => onGenerate({ stage, deponent, instructions })} />
      <div>
        <button type="button" className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" onClick={() => setByHand((v) => !v)} disabled={running} data-testid="ws-byhand-toggle">
          {byHand ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} or build it by hand from the template blocks
        </button>
        {byHand && (
          <div className="mt-2 space-y-2" data-testid="ws-byhand">
            <div className="text-[10px] text-slate-500">Standard paragraphs filled from the file — missing facts show as [placeholders]. The AI can still regenerate or improve them on the Draft step.</div>
            <BlockPicker blocks={ctx.blocks} stage={stage} picked={picked} setPicked={setPicked} />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => create([])} disabled={creating || running} data-testid="ws-create-blank">Start blank</Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => create(picked)} disabled={creating || running || !deponentName} data-testid="ws-create">
                {creating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}{creating ? "Reading the file…" : `Create from ${picked.length} block${picked.length === 1 ? "" : "s"} →`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
